import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { CompletionMetric } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import type { ComponentProps, ReactNode } from 'react'
import { useCallback, useMemo, useState, useId } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type { ButtonKind } from 'components/design-system/BreakoutButton'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { useTranslationTyped } from 'i18n/i18n'
import classNames from 'classnames'
import type {
  AssignmentFormValues,
  LtiAssignmentFormValues,
} from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import type { DateTime } from 'luxon'
import { ChevronLeft } from 'components/icons/ChevronLeft'
import { ChevronRight } from 'components/icons/ChevronRight'
import { FormStage } from 'pages/instructor/slide_deck/AssignmentDialog/types'
import { checkScalarsAreEqual } from 'pages/instructor/slide_deck/AssignmentDialog/helpers'
import { DateInputs } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/DateInputs'
import { GroupingInputs } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/GroupingInputs'
import { GradingInputs } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/GradingInputs'
import { PeerReviewInputs } from 'pages/instructor/slide_deck/AssignmentDialog/BaseAssignmentDialog/PeerReviewInputs'
import { isPeerReviewAllowed } from '@breakoutlearning/firebase-repository/sources-of-truth/isPeerReviewAllowed'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useCubitBuilder } from 'hooks/cubits'
import { CreateOrEditAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/CreateOrEditAssignmentCubit'
import { useRepository } from 'hooks/auth'
import { Spinner } from 'components/Spinner'
import { type SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import { observer } from 'mobx-react-lite'

export const BaseAssignmentDialog = observer(function BaseAssignmentDialog({
  assignment,
  sectionEndDate,
  sectionStartDate,
  className,
  closeCallback,
  earlySecondaryButtonOverride,
  dueDateInitialView,
  assignedAtInitialView,
  form,
  forcedStage,
  hasBeenStarted,
  hasQuestions,
  hasRubrics,
  initialAssignmentGradingScalars,
  isDraft = false,
  onSubmit,
  onSubmitStage,
  releasedToStudents,
  scalarsRef,
  sectionSelectComponent,
  submitButtonText,
  submitButtonIncludeChevron = false,
  disableGroupingReason,
  gradingSumValidOverride,
  currentScalarsToCheckDirty = undefined,
  readOnly = false,
  slideDeckId,
  roles,
  onLtiGradingLineItemIdChange,
  individualExtensionsComponent,
  advancedGradingSlot,
  onCustomizeWeights,
  customRubricsValid = true,
  completionMetric,
  onCompletionMetricChange,
}: {
  assignment?: SectionAssignment
  sectionEndDate?: DateTime
  sectionStartDate?: DateTime
  className?: string
  closeCallback?: () => void
  earlySecondaryButtonOverride?: {
    text: string
    kind?: ButtonKind
    onClick: () => Promise<void>
  }
  dueDateInitialView?: DateTime
  assignedAtInitialView?: DateTime
  onSubmitStage?: (
    stage: FormStage,
    data: Partial<AssignmentFormValues>
  ) => Promise<void>
  form: UseFormReturn<AssignmentFormValues>
  forcedStage?: FormStage
  disableGroupingReason?: string
  hasBeenStarted: boolean
  hasQuestions: boolean
  hasRubrics: boolean
  initialAssignmentGradingScalars?: SectionAssignment['data']['assignmentGradingScalars']
  isDraft?: boolean
  onSubmitLTIOverride?: (data: LtiAssignmentFormValues) => Promise<void>
  onSubmit: (data: AssignmentFormValues) => Promise<void>
  releasedToStudents: boolean
  scalarsRef: React.RefObject<
    SectionAssignment['data']['assignmentGradingScalars']
  >
  sectionSelectComponent?: ReactNode
  submitButtonText: string
  submitButtonIncludeChevron?: boolean
  gradingSumValidOverride?: {
    gradingSumValid: boolean
    gradingSum: number
    setGradingSumCallback: ({
      isValid,
      sumAsInt,
    }: {
      isValid: boolean
      sumAsInt: number
    }) => void
  }
  /** The current grading scalars to check for dirtiness.
   * undefined = no check
   * null = check against no scalars
   */
  currentScalarsToCheckDirty?:
    | SectionAssignment['data']['assignmentGradingScalars']
    | null
  readOnly?: boolean
  slideDeckId: string
  roles?: SlideDeckRole[]
  onLtiGradingLineItemIdChange?: (ltiGradingLineItemId: string) => void
  /** rendered below the date inputs on the Dates stage (edit flow only) */
  individualExtensionsComponent?: ReactNode
  advancedGradingSlot?: React.ReactNode
  /** Open the custom-rubric weights dialog (the score-mapping panel itself is
   * always shown when the deck has eligible rubrics). */
  onCustomizeWeights?: () => void
  /** whether the custom rubric config (score mapping + weights) is currently valid */
  customRubricsValid?: boolean
  /** How Completion is measured. Absent = legacy time in session. */
  completionMetric?: CompletionMetric
  /** When provided, the Completion metric toggle renders in the Grading stage. */
  onCompletionMetricChange?: (metric: CompletionMetric) => void
}) {
  const [stage, setStage] = useState<FormStage>(forcedStage ?? FormStage.Dates)
  const [isLoading, setIsLoading] = useState(false)

  const [isLoadingEarlySecondary, setIsLoadingEarlySecondary] = useState(false)
  const { tt } = useTranslationTyped()
  const headingId = useId()

  // if the parent component is supplying override, use the parent function
  // and values for gradingSum / setGrading sum, else use local state
  const [
    { gradingSumValid: _gradingSumValid, gradingSum: _gradingSum },
    _setGradingSum,
  ] = useState<{
    gradingSumValid: boolean
    gradingSum: number
  }>({ gradingSumValid: false, gradingSum: 0 })

  const gradingSumValid =
    gradingSumValidOverride?.gradingSumValid ?? _gradingSumValid
  const gradingSum = gradingSumValidOverride?.gradingSum ?? _gradingSum

  const setGradingSumCallback = useCallback(
    ({ isValid, sumAsInt }: { isValid: boolean; sumAsInt: number }) => {
      if (gradingSumValidOverride?.setGradingSumCallback)
        return gradingSumValidOverride.setGradingSumCallback({
          isValid,
          sumAsInt,
        })

      _setGradingSum({
        gradingSum: sumAsInt,
        gradingSumValid: isValid,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gradingSumValidOverride?.setGradingSumCallback]
  )

  const [initialAssignedAt, initialDueDate] = useMemo(
    () => [form.getValues('assignedAt'), form.getValues('dueDate')],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const {
    control,
    setValue,
    getValues,
    formState: { isSubmitting, isValid: formValid, dirtyFields },
    trigger,
    handleSubmit,
    watch,
  } = form

  const [scalarsDirty, setScalarsDirty] = useState(
    currentScalarsToCheckDirty !== undefined
  )
  const configureGrading = watch('configureGrading')
  const groupingType = watch('groupingType')
  const dueDate = watch('dueDate')
  const assignedAt = watch('assignedAt')
  const allowLateSubmissions = watch('allowLateSubmissions')

  const customGroupingSelected = watch('customGroupingSelected')
  const forbidStudentGroupCreation = watch('forbidStudentGroupCreation')
  const forbidStudentGroupScheduling = watch('forbidStudentGroupScheduling')
  const forbidStudentGroupJoining = watch('forbidStudentGroupJoining')
  const forbidStudentGroupLeaving = watch('forbidStudentGroupLeaving')
  const peerReviewsEnabled = watch('peerReviewsEnabled')
  const groupingSize = watch('groupingSize')
  const showFinalGradeToParticipants = watch('showFinalGradeToParticipants')

  // The custom-rubric panel (score mapping) is always shown when the deck has
  // eligible rubrics and grading is configured; this widens the grading stage.
  const wideGrading =
    stage === FormStage.Grading && !!advancedGradingSlot && !!configureGrading

  const showFinalGradeChanged =
    !assignment ||
    assignment.data.showFinalGradeToParticipants !==
      showFinalGradeToParticipants

  const isPeerReviewDisabled =
    !isPeerReviewAllowed(groupingSize) && stage === FormStage.PeerReviews

  const onGradingChange = useCallback(
    ({
      scalars,
      configureGrading,
    }: {
      scalars: SectionAssignment['data']['assignmentGradingScalars'] | null
      configureGrading: boolean
    }) => {
      if (currentScalarsToCheckDirty === undefined) return setScalarsDirty(true) // not checking

      // if we went from no scalars to scalars or vice versa, dirty
      if (configureGrading !== !!currentScalarsToCheckDirty)
        return setScalarsDirty(true)

      const equal = checkScalarsAreEqual(scalars, currentScalarsToCheckDirty)
      setScalarsDirty(!equal)
    },
    [currentScalarsToCheckDirty]
  )

  // A Completion-metric-only change (weights unchanged) must still enable Save,
  // so it isn't blocked by the scalars-dirty gate below.
  const completionMetricChanged =
    (completionMetric ?? CompletionMetric.timeInSession) !==
    (assignment?.data.completionMetric ?? CompletionMetric.timeInSession)

  const availableStages = useMemo<FormStage[]>(() => {
    const stages = []
    stages.push(FormStage.Dates)
    stages.push(FormStage.Grouping)
    if (peerReviewsEnabled || stage === FormStage.PeerReviews) {
      stages.push(FormStage.PeerReviews)
    }
    stages.push(FormStage.Grading)
    return stages
  }, [peerReviewsEnabled, stage])

  const currentStageIndex = availableStages.indexOf(stage)

  const previousStage =
    currentStageIndex > 0 ? availableStages[currentStageIndex - 1] : null

  const nextStage =
    currentStageIndex < availableStages.length - 1
      ? availableStages[currentStageIndex + 1]
      : null

  const getFieldsToSave = useCallback(
    async ({ validate }: { validate?: boolean }) => {
      const fieldsToSave: (keyof AssignmentFormValues)[] = (() => {
        switch (stage) {
          case FormStage.Dates:
            return [
              'assignedAt',
              'dueDate',
              'allowLateSubmissions',
              'lateSubmissionCutoff',
            ]
          case FormStage.Grouping:
            return [
              'customGroupingSelected',
              'groupingType',
              'groupingSize',
              'groupingSizeMinimum',
              'groupingSizeMaximum',
              'forbidStudentGroupCreation',
              'forbidStudentGroupSelection',
              'forbidStudentGroupScheduling',
              'forbidStudentGroupJoining',
              'forbidStudentGroupLeaving',
              'peerReviewsEnabled',
            ]
          case FormStage.PeerReviews:
            return [
              'peerReviewCriteria',
              'peerReviewExcludesSelfReview',
              'peerReviewsHiddenFromReviewees',
            ]
          case FormStage.Grading:
            return ['configureGrading', 'showFinalGradeToParticipants']
        }
      })()

      if (validate) {
        const isValid = await trigger(fieldsToSave)

        if (!isValid) {
          return false
        }
      }

      // get only the we care about
      const data = getValues(fieldsToSave)
      const toSave = fieldsToSave.reduce<Partial<AssignmentFormValues>>(
        (acc, cur, index) => {
          // @ts-expect-error - we know the types are correct
          acc[cur] = data[index]
          return acc
        },
        {}
      )

      return toSave
    },
    [getValues, stage, trigger]
  )

  const repository = useRepository()

  const cubit = useCubitBuilder(
    () =>
      new CreateOrEditAssignmentCubit({
        repository,
        slideDeckId,
        roles,
      }),
    [repository, slideDeckId, roles]
  )

  const dialogProps: Omit<ComponentProps<typeof Dialog>, 'children'> = {
    size: 'xl' as const,
    testId: 'assign-class-dialog',
    className: classNames(
      'flex h-[750px] w-[1020px] items-center justify-center !overflow-hidden',
      className
    ),
    innerClassName:
      'flex flex-col h-full w-full items-center gap-4 !min-h-0 !p-10 overflow-auto',
    ignoreBackdropClick: true,
    onDismiss: closeCallback,
    dismissOnEscapeKey: true,
    'aria-labelledby': headingId,
  }

  if (!cubit.ready)
    return (
      <Dialog {...dialogProps}>
        <DialogCloseButton
          onClick={() => {
            if (closeCallback) {
              closeCallback()
              return false
            }
            return true
          }}
        />
        <Spinner />
      </Dialog>
    )

  return (
    <Dialog {...dialogProps}>
      <DialogCloseButton
        onClick={() => {
          if (closeCallback) {
            closeCallback()
            return false
          }
          return true
        }}
      />

      <div className={'flex flex-col gap-1 text-center'}>
        {!forcedStage && (
          <strong className="text-body-large text-on-surface-var">
            {(() => {
              if (currentStageIndex === 0) {
                return tt.instructor_library.step_1()
              } else if (currentStageIndex === 1) {
                return tt.instructor_library.step_2()
              } else if (currentStageIndex === 2) {
                return tt.instructor_library.step_3()
              } else {
                return tt.instructor_library.step_4()
              }
            })()}
          </strong>
        )}
        <h1 id={headingId} className="text-headline-large">
          {forcedStage === FormStage.Dates &&
            tt.instructor_assignment.configure_assignment_dialog.edit_assignment_dates()}
          {forcedStage === FormStage.Grouping &&
            tt.instructor_assignment.configure_assignment_dialog.edit_grouping()}
          {forcedStage === FormStage.PeerReviews &&
            tt.instructor_assignment.configure_assignment_dialog.edit_peer_reviews()}
          {forcedStage === FormStage.Grading &&
            tt.instructor_assignment.configure_assignment_dialog.edit_grading()}
          {!forcedStage &&
            stage === FormStage.Dates &&
            tt.instructor_assignment.configure_assignment_dialog.configure_assignment_dates()}
          {!forcedStage &&
            stage === FormStage.Grouping &&
            tt.instructor_assignment.configure_assignment_dialog.configure_grouping()}
          {!forcedStage &&
            stage === FormStage.PeerReviews &&
            tt.instructor_assignment.configure_assignment_dialog.configure_peer_reviews()}
          {!forcedStage &&
            stage === FormStage.Grading &&
            tt.instructor_assignment.configure_assignment_dialog.configure_grading()}
        </h1>
      </div>

      <form
        className={classNames(
          'flex min-h-0 flex-grow flex-col items-center gap-2',
          {
            'w-[450px]':
              ![FormStage.Grouping, FormStage.PeerReviews].includes(stage) &&
              !wideGrading,
            'w-[880px]': wideGrading,
          }
        )}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex w-full flex-grow flex-col gap-1 px-0.5 pb-1">
          {sectionSelectComponent && stage === FormStage.Dates
            ? sectionSelectComponent
            : null}

          {stage === FormStage.Dates && (
            <DateInputs
              assignedAtInitialView={assignedAtInitialView}
              dueDateInitialView={dueDateInitialView}
              control={control}
              initialAssignedAt={initialAssignedAt}
              initialDueDate={initialDueDate}
              isSubmitting={isSubmitting}
              hasBeenStarted={hasBeenStarted}
              isDraft={isDraft}
              assignedAt={assignedAt}
              releasedToStudents={releasedToStudents}
              groupingType={groupingType}
              dueDate={dueDate}
              formValid={formValid}
              readOnly={readOnly}
              allowLateSubmissions={allowLateSubmissions}
              sectionEndDate={sectionEndDate}
              sectionStartDate={sectionStartDate}
              setValue={setValue}
              trigger={trigger}
            />
          )}

          {stage === FormStage.Dates && individualExtensionsComponent}

          {stage === FormStage.Grouping && (
            <GroupingInputs
              form={form}
              groupingType={groupingType}
              customGroupingSelected={customGroupingSelected}
              forbidStudentGroupCreation={forbidStudentGroupCreation}
              forbidStudentGroupScheduling={forbidStudentGroupScheduling}
              forbidStudentGroupJoining={forbidStudentGroupJoining}
              forbidStudentGroupLeaving={forbidStudentGroupLeaving}
              disableGroupingReason={disableGroupingReason}
              readOnly={readOnly}
              isForcedStage={forcedStage === FormStage.Grouping}
              cubit={cubit}
            />
          )}

          {stage === FormStage.PeerReviews && (
            <PeerReviewInputs form={form} readOnly={readOnly} />
          )}

          {stage === FormStage.Grading && (
            <GradingInputs
              assignment={assignment}
              setValue={setValue}
              configureGrading={configureGrading}
              releasedToStudents={releasedToStudents}
              hasQuestions={hasQuestions}
              hasRubrics={hasRubrics}
              scalarsRef={scalarsRef}
              initialAssignmentGradingScalars={initialAssignmentGradingScalars}
              setGradingSumCallback={setGradingSumCallback}
              gradingSumValid={gradingSumValid}
              gradingSum={gradingSum}
              onChange={onGradingChange}
              readOnly={readOnly}
              onLtiGradingLineItemIdChange={
                onLtiGradingLineItemIdChange
                  ? (ltiGradingLineItemId) => {
                      setValue('ltiGradingLineItemId', ltiGradingLineItemId, {
                        shouldDirty: true,
                      })
                      onLtiGradingLineItemIdChange(ltiGradingLineItemId)
                    }
                  : undefined
              }
              showFinalGradeToParticipants={
                showFinalGradeToParticipants ?? true
              }
              onShowFinalGradeToParticipantsChange={(show) => {
                setValue('showFinalGradeToParticipants', show)
              }}
              advancedGradingSlot={advancedGradingSlot}
              onCustomizeWeights={onCustomizeWeights}
              completionMetric={completionMetric}
              onCompletionMetricChange={onCompletionMetricChange}
            />
          )}
        </div>

        <div className="flex w-[450px] flex-col justify-end">
          <div
            className={classNames('mt-2 flex w-full flex-row gap-2', {
              'justify-end': forcedStage === undefined,
              'justify-between': forcedStage !== undefined,
            })}
          >
            {forcedStage === undefined && previousStage !== null && (
              <BreakoutButton
                className="mr-auto !w-[120px] !min-w-[120px]"
                icon={<ChevronLeft size={15} />}
                kind="secondary"
                size="medium"
                disabled={
                  readOnly ||
                  (!forcedStage &&
                    stage === FormStage.PeerReviews &&
                    !formValid) // Since peer review criteria are part of the form state rather than a separate useState, we have to lock you on this stage if they're invalid
                }
                onClick={() => {
                  if (previousStage) {
                    setStage(previousStage)
                  }
                }}
              >
                {tt.instructor_library.back()}
              </BreakoutButton>
            )}

            {(!isDraft || forcedStage) && (
              <BreakoutButton
                size="medium"
                type="button"
                className="!w-[120px] !min-w-[120px] text-nowrap"
                disabled={isLoading}
                kind={earlySecondaryButtonOverride?.kind ?? 'secondary'}
                data-testid="assign-class-secondary-button"
                onClick={
                  earlySecondaryButtonOverride
                    ? async () => {
                        setIsLoadingEarlySecondary(true)
                        await earlySecondaryButtonOverride?.onClick()
                        setIsLoadingEarlySecondary(false)
                      }
                    : closeCallback
                }
                loading={isLoadingEarlySecondary}
              >
                {earlySecondaryButtonOverride?.text ??
                  tt.instructor_library.cancel()}
              </BreakoutButton>
            )}

            {forcedStage === undefined && nextStage !== null && (
              <>
                <BreakoutTooltip
                  enabled={isPeerReviewDisabled}
                  content={tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one()}
                >
                  <div>
                    <BreakoutButton
                      disabled={
                        isLoadingEarlySecondary ||
                        readOnly ||
                        isPeerReviewDisabled
                      }
                      data-testid="assign-class-continue-button"
                      size="medium"
                      type="button"
                      kind="accent"
                      className="!w-[120px] !min-w-[120px]"
                      onClick={async () => {
                        setIsLoading(true)

                        const isValid = await trigger(undefined, {
                          shouldFocus: true,
                        })

                        setIsLoading(false)

                        if (!isValid) return

                        if (onSubmitStage) {
                          const toSave = await getFieldsToSave({
                            validate: false,
                          })

                          if (toSave === false) return

                          // don't await, blocks form progression in UI
                          onSubmitStage(stage, toSave)
                        }

                        if (nextStage) {
                          setStage(nextStage)
                        }
                      }}
                      loading={isLoading}
                    >
                      {tt.instructor_library.continue()}
                    </BreakoutButton>
                  </div>
                </BreakoutTooltip>
              </>
            )}
            {forcedStage === undefined && nextStage === null && (
              <BreakoutButton
                size="medium"
                type="button"
                kind="accent"
                className="!w-[120px] !min-w-[120px]"
                disabled={
                  configureGrading === undefined ||
                  (configureGrading && !gradingSumValid) ||
                  (configureGrading &&
                    !!advancedGradingSlot &&
                    !customRubricsValid) ||
                  isLoading ||
                  readOnly
                }
                onClick={async (e) => {
                  e.preventDefault()
                  setIsLoading(true)

                  return await handleSubmit((data) => onSubmit(data))().finally(
                    () => {
                      setIsLoading(false)
                    }
                  )
                }}
                data-testid="assign-class-button"
                loading={isSubmitting}
                iconOnRight={true}
                icon={
                  submitButtonIncludeChevron ? (
                    <ChevronRight size={15} />
                  ) : undefined
                }
              >
                {submitButtonText}
              </BreakoutButton>
            )}
            {forcedStage !== undefined && onSubmitStage && !readOnly && (
              <BreakoutButton
                size="medium"
                type="button"
                kind="accent"
                className="!w-[120px] !min-w-[120px]"
                disabled={
                  /*(stage === FormStage.Grading && releasedToStudents) ||*/
                  (stage === FormStage.Grading &&
                    configureGrading &&
                    !gradingSumValid) ||
                  (stage === FormStage.Grading &&
                    configureGrading &&
                    !!advancedGradingSlot &&
                    !customRubricsValid) ||
                  (stage === FormStage.Grading &&
                    !advancedGradingSlot &&
                    !scalarsDirty &&
                    !completionMetricChanged &&
                    !dirtyFields.ltiGradingLineItemId &&
                    !showFinalGradeChanged) ||
                  (peerReviewsEnabled && groupingSize === 1) ||
                  isLoading ||
                  readOnly
                }
                onClick={async (e) => {
                  e.preventDefault()
                  setIsLoading(true)

                  try {
                    const toSave = await getFieldsToSave({ validate: true })

                    if (toSave === false) {
                      setIsLoading(false)
                      return
                    }

                    await onSubmitStage(stage, toSave)
                    // await forceStageForEdit.onSubmitPartial(toSave)
                  } finally {
                    setIsLoading(false)
                  }
                }}
                data-testid="assign-class-button"
                loading={isSubmitting}
              >
                {submitButtonText}
              </BreakoutButton>
            )}
          </div>
        </div>
      </form>
    </Dialog>
  )
})
